import { useQuery } from '@tanstack/react-query';
import { Crown, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type HandedOnWork, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { anyFilter, useKept, useKeptFilters } from '../lib/kept-filters';
import { ChecklistProgressChip } from '../components/TaskChecklist';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  priorityTone,
  statusTone,
  type BadgeTone,
} from '../components/ui';

const STATUSES = ['TO_DO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE', 'CANCELLED'];

/** Where the work handed on from a Leaders Task stands, as its badge says it. */
export const WORK_LABEL: Record<HandedOnWork, TranslationKey> = {
  none: 'leaderTasks.work.none',
  underWay: 'leaderTasks.work.underWay',
  finished: 'leaderTasks.work.finished',
  calledOff: 'leaderTasks.work.calledOff',
};

export function workTone(work: HandedOnWork): BadgeTone {
  return work === 'finished' ? 'success' : work === 'underWay' ? 'info' : work === 'calledOff' ? 'warning' : 'neutral';
}

/** Department leaders send and receive Leaders Tasks; the admins send them and see them all. */
export function useLeaderTaskRole() {
  const { can, ledDepartmentIds } = useAuth();
  const oversees = can(P.TASKS_MANAGE_ALL);
  return { oversees, leads: ledDepartmentIds.length > 0, maySend: oversees || ledDepartmentIds.length > 0 };
}

type Box = 'received' | 'sent' | 'all';

/**
 * Route: `/leader-tasks` — Leaders Tasks (operator, 2026-10-07): from one
 * department leader to another. A leader sees those asked of them and those
 * they sent; the admins see them all. Each row says where the work handed on
 * from it stands.
 */
export function LeaderTasksPage() {
  const { t, locale, localized } = useI18n();
  const { oversees, leads, maySend } = useLeaderTaskRole();
  const navigate = useNavigate();
  // The tab and the filters are kept until cleared, so coming back from a task finds the list as it was left.
  const [keptBox, setBox] = useKept<Box>('leader-tasks.box', leads || !oversees ? 'received' : 'all');
  // All is the admins' tab; one kept from before the role changed opens Received.
  const box = keptBox === 'all' && !oversees ? 'received' : keptBox;
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useKeptFilters('leader-tasks');

  const setFilter = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leader-tasks', box, page, filters],
    queryFn: () => api.get<Paginated<any>>('/leader-tasks', { box, page, pageSize: 25, ...filters }),
  });

  const tabs: Array<{ id: Box; label: string }> = [
    { id: 'received', label: t('leaderTasks.received') },
    { id: 'sent', label: t('leaderTasks.sent') },
    ...(oversees ? [{ id: 'all' as const, label: t('leaderTasks.all') }] : []),
  ];

  return (
    <>
      <PageHeader
        title={t('leaderTasks.title')}
        subtitle={t('leaderTasks.subtitle')}
        count={data?.total}
        action={
          maySend && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/leader-tasks/new')}>
              {t('leaderTasks.new')}
            </Button>
          )
        }
      />

      <div className="mb-4">
        <Tabs
          tabs={tabs}
          active={box}
          onChange={(id) => {
            setBox(id as Box);
            setPage(1);
          }}
        />
      </div>

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <Input placeholder={t('common.search')} value={filters.q ?? ''} onChange={(e) => setFilter('q', e.target.value)} />
          <Select value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <Select value={filters.priority ?? ''} onChange={(e) => setFilter('priority', e.target.value)}>
            <option value="">{t('common.priority')} — {t('common.all')}</option>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 border-t hairline px-4 py-2.5">
          <Button
            size="sm"
            variant={filters.overdue === 'true' ? 'danger' : 'ghost'}
            onClick={() => setFilter('overdue', filters.overdue === 'true' ? '' : 'true')}
          >
            {t('tickets.overdue')}
          </Button>
          {anyFilter(filters) && (
            <Button size="sm" variant="ghost" onClick={() => { setFilters({}); setPage(1); }}>
              {t('common.clear')}
            </Button>
          )}
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Crown className="h-8 w-8" />}
            title={box === 'sent' ? t('leaderTasks.emptySent') : box === 'received' ? t('leaderTasks.emptyReceived') : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('leaderTasks.from')}</Th>
                  <Th>{t('leaderTasks.to')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('leaderTasks.work')}</Th>
                  <Th align="end">{t('common.dueDate')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((task) => {
                  const overdue =
                    task.dueAt && new Date(task.dueAt) < new Date() && !['DONE', 'CANCELLED'].includes(task.status);
                  return (
                    <Tr key={task.id} onClick={() => navigate(`/leader-tasks/${task.id}`)}>
                      <Td className="max-w-sm">
                        <span className="block truncate" dir="auto">{task.title}</span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <Badge tone={priorityTone(task.priority)}>{enumLabel(task.priority, locale)}</Badge>
                          {task.project && <span className="truncate text-caption text-muted">{localized(task.project)}</span>}
                          <ChecklistProgressChip progress={task.checklistProgress} />
                        </span>
                      </Td>
                      <Td className="text-muted">{localized(task.createdBy)}</Td>
                      <Td>
                        <span className="block text-[var(--text-primary)]">{task.assignee ? localized(task.assignee) : '—'}</span>
                        {task.department && <span className="block text-caption text-muted">{localized(task.department)}</span>}
                      </Td>
                      <Td>
                        <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
                      </Td>
                      <Td>
                        {task.work === 'none' ? (
                          <span className="text-caption text-faint">{t(WORK_LABEL.none)}</span>
                        ) : (
                          <Badge tone={workTone(task.work)}>{t(WORK_LABEL[task.work as HandedOnWork])}</Badge>
                        )}
                      </Td>
                      <Td align="end" className={overdue ? 'text-error-fg dark:text-error-dark tabular' : 'text-muted tabular'}>
                        {task.dueAt ? formatDate(task.dueAt, locale) : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
